import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, Typography, LinearProgress, Alert } from '@mui/material';
import { SnackbarProvider } from 'notistack';

import { AuthProvider, useAuth } from './context/AuthContext';
import beastTheme from './theme/beastTheme';

import Login from './pages/Login';
import LoginTest from './pages/LoginTest';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import AssetForm from './pages/AssetForm';
import PhysicalAssetForm from './pages/PhysicalAssetForm';
import TokenAssetForm from './pages/TokenAssetForm';
import LicenseAssetForm from './pages/LicenseAssetForm';
import AssetList from './pages/AssetList';
import LicenseForm from './pages/LicenseForm';
import LicenseList from './pages/LicenseList';
import CertificateForm from './pages/CertificateForm';
import CertificateList from './pages/CertificateList';
import DomainForm from './pages/DomainForm';
import DomainList from './pages/DomainList';
import DispositivosHomologadosList from './pages/DispositivosHomologadosList';
import TestPage from './pages/TestPage';
import DebugPage from './pages/DebugPage';
import AuditLogs from './pages/AuditLogs';
import Reports from './pages/Reports';
import Alerts from './pages/Alerts';

const FullscreenLoading = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', p: 3 }}>
    <Box sx={{ width: '100%', maxWidth: 380 }}>
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        Carregando sistema...
      </Typography>
      <LinearProgress />
    </Box>
  </Box>
);

const ProtectedRouteContent = ({ children }) => {
  const location = useLocation();
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <FullscreenLoading />;
  }

  if (isAuthenticated && user) {
    return children;
  }

  return <Navigate to="/login" state={{ from: location }} replace />;
};

const ProtectedRoute = ({ children }) => <ProtectedRouteContent>{children}</ProtectedRouteContent>;

const ProtectedRoleRoute = ({ children, allowedRoles = [] }) => {
  const { user } = useAuth();

  if (!user || !allowedRoles.includes(user.role)) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Você não tem permissão para acessar esta página.</Alert>
      </Box>
    );
  }

  return children;
};

const AppRoute = ({ element, allowedRoles }) => (
  <ProtectedRoute>
    {allowedRoles ? (
      <ProtectedRoleRoute allowedRoles={allowedRoles}>{element}</ProtectedRoleRoute>
    ) : (
      element
    )}
  </ProtectedRoute>
);

function App() {
  return (
    <ThemeProvider theme={beastTheme}>
      <CssBaseline />
      <SnackbarProvider maxSnack={3}>
        <Router>
          <AuthProvider>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/login-test" element={<LoginTest />} />

              <Route path="/dashboard" element={<AppRoute element={<Dashboard />} />} />
              <Route path="/users" element={<AppRoute element={<Users />} />} />

              <Route path="/assets/physical/new" element={<AppRoute element={<PhysicalAssetForm />} />} />
              <Route path="/assets/token/new" element={<AppRoute element={<TokenAssetForm />} />} />
              <Route path="/assets/license/new" element={<AppRoute element={<LicenseAssetForm />} />} />

              <Route path="/assets/physical/:id/edit" element={<AppRoute element={<PhysicalAssetForm />} />} />
              <Route path="/assets/token/:id/edit" element={<AppRoute element={<TokenAssetForm />} />} />
              <Route path="/assets/license/:id/edit" element={<AppRoute element={<LicenseAssetForm />} />} />

              <Route path="/assets/new/api" element={<Navigate to="/assets/token/new" replace />} />
              <Route path="/assets/category/api" element={<Navigate to="/assets/category/token" replace />} />
              <Route path="/assets/new/:category?" element={<AppRoute element={<AssetForm />} />} />
              <Route path="/assets/:id/edit" element={<AppRoute element={<AssetForm />} />} />
              <Route path="/assets/category/:category" element={<AppRoute element={<AssetList />} />} />

              <Route path="/apis" element={<Navigate to="/assets/category/token" replace />} />
              <Route path="/apis/new" element={<Navigate to="/assets/token/new" replace />} />
              <Route path="/apis/:id/edit" element={<Navigate to="/assets/category/token" replace />} />
              <Route path="/assets/api/new" element={<Navigate to="/assets/token/new" replace />} />
              <Route path="/assets/api/:id/edit" element={<Navigate to="/assets/category/token" replace />} />

              <Route path="/ai-licenses" element={<Navigate to="/licenses?license_kind=ai" replace />} />
              <Route path="/licenses" element={<AppRoute element={<LicenseList />} />} />
              <Route path="/licenses/new" element={<AppRoute element={<LicenseForm />} />} />
              <Route path="/licenses/:id/edit" element={<AppRoute element={<LicenseForm />} />} />

              <Route path="/certificates" element={<AppRoute element={<CertificateList />} />} />
              <Route path="/certificates/new" element={<AppRoute element={<CertificateForm />} />} />
              <Route path="/certificates/:id/edit" element={<AppRoute element={<CertificateForm />} />} />

              <Route path="/domains" element={<AppRoute element={<DomainList />} />} />
              <Route path="/domains/new" element={<AppRoute element={<DomainForm />} />} />
              <Route path="/domains/:id/edit" element={<AppRoute element={<DomainForm />} />} />

              <Route path="/homologated-devices" element={<AppRoute element={<DispositivosHomologadosList />} />} />

              <Route path="/test" element={<AppRoute element={<TestPage />} />} />
              <Route path="/debug" element={<AppRoute element={<DebugPage />} />} />
              <Route path="/audit" element={<AppRoute element={<AuditLogs />} />} />

              <Route
                path="/reports"
                element={<AppRoute element={<Reports />} allowedRoles={['admin', 'manager']} />}
              />
              <Route
                path="/reports/new"
                element={<AppRoute element={<Reports />} allowedRoles={['admin', 'manager']} />}
              />
              <Route
                path="/alerts"
                element={<AppRoute element={<Alerts />} allowedRoles={['admin', 'manager']} />}
              />

              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </AuthProvider>
        </Router>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

export default App;
